import { renderToStaticMarkup } from "react-dom/server";
import { LocalizeContextProps } from "react-localize-redux";
import { Logger } from "./Logger";
import { setRoamingItem, setLocalStorageItem } from "../components/app/AppComponent";
import { getEnglishTranslation } from "../i18n/getEnglishTranslation";
const englishTranslations = require("./../i18n/en.json");
const germanTranslations = require("./../i18n/de.json");
const pseudoTranslations = require("./../i18n/af.json");

export enum LanguageCode {
    English = "en",
    French = "fr",
    German = "de",
    Pseudo = "af",
}

export class TranslationService {
    constructor(private logger: Logger) {}

    // Resolves a dotted translationId (e.g. "FILE_MULTIPLE_EMAIL.ERROR_LOADING_EMAILS")
    // against the bundled English translations, which are always available synchronously.
    private resolveEnglishTranslation(translationId: string): string | undefined {
        const value = translationId
            .split(".")
            .reduce<any>((current, key) => (current == null ? undefined : current[key]), englishTranslations);
        return typeof value === "string" ? value : undefined;
    }

    getTranslations(props: LocalizeContextProps) {
        // NOTE: For help with i18n, see https://ryandrewjohnson.github.io/react-localize-redux-docs/
        props.initialize({
            languages: [
                { name: "English", code: LanguageCode.English },
                { name: "French", code: LanguageCode.French },
                { name: "German", code: LanguageCode.German },
                { name: "Pseudo", code: LanguageCode.Pseudo },
            ],
            options: {
                renderToStaticMarkup,
                defaultLanguage: LanguageCode.English,
                // A translation may be requested before the active language's strings have
                // finished loading (e.g. an error toast fired during a rapid close/reopen of
                // the add-in). Without this handler react-localize-redux renders its raw
                // "Missing translationId: ... for language: ${ languageCode }" placeholder.
                // Fall back to the bundled English string so users always see readable text.
                onMissingTranslation: ({ translationId }) => {
                    const fallback = this.resolveEnglishTranslation(translationId);
                    if (fallback !== undefined) {
                        return fallback;
                    }
                    this.logger.error(`TranslationService. Missing translation for "${translationId}".`);
                    return "";
                },
            },
        });
        const isClassicOutlook =
            Office.context && (Office.context.platform === Office.PlatformType.PC || Office.context.platform == null);
        let language: string = LanguageCode.English;
        try {
            language = Office.context.displayLanguage.substr(0, 2);
            if (language) {
                isClassicOutlook ? setRoamingItem("language", language) : setLocalStorageItem("language", language);
            }
        } catch (error) {
            this.logger.error(
                `TranslationService. Failed to load translation strings from ${Office.context.displayLanguage}. Returning english strings.`,
                error
            );
            props.addTranslationForLanguage(englishTranslations, LanguageCode.English);
            return;
        }
        switch (language) {
            case LanguageCode.English:
                props.addTranslationForLanguage(englishTranslations, language);
                break;
            // this is being indefinitely disabled due to limited customer usage in the other languages
            // see issue: https://newforma.atlassian.net/browse/PROG-43860
            // case LanguageCode.French:
            //     props.addTranslationForLanguage(frenchTranslations, LanguageCode.French);
            //     break;
            case LanguageCode.German:
                props.addTranslationForLanguage(germanTranslations, language);
                break;
            case LanguageCode.Pseudo:
                props.addTranslationForLanguage(pseudoTranslations, language);
                break;
            default:
                this.logger.info(`unsupported language ${language} defaulting to english`);
                language = LanguageCode.English;
                props.addTranslationForLanguage(englishTranslations, language);
                break;
        }
        this.logger.info(`setting language to ${language}`);
        props.setActiveLanguage(language);
    }
}
